import { Dispatch as ReduxDispatch } from "@reduxjs/toolkit";
import { Button, ToastManager } from "@tvg/design-system";
import React, { Dispatch, SetStateAction } from "react";
import { closeBetaModal, openBetaModal } from "@tvg/sh-lib-beta-program";
import { ModalAction, PAGE_ACTION } from "@tvg/sh-lib-beta-program/types/redux";
import {
  enrollInBeta,
  hasDismissedBetaFeatureEnroll,
  isEligible,
  isFeatureInCooldown,
  isOptedInBetaFeature,
  saveDismissStatus,
  saveWelcomedStatus,
  clearStatusFromFeature,
  hasBeenWelcomedOnBetaFeature
} from "@tvg/sh-lib-beta-program/utils/handlers";
import { ButtonVariant } from "@tvg/design-system/src/components/button/types";
import { NullaryFn } from "@tvg/ts-types/Functional";
import { User } from "@tvg/ts-types/Login";
import {
  gtmClickCTALearnMoreModal,
  gtmClickCTAOnboardingModal,
  gtmViewLearnMoreModal,
  gtmViewOnboardingModal,
  gtmViewWelcomeModal,
  gtmClickCTALetsStartModal
} from "@tvg/sh-lib-beta-program/gtm";
import {
  BetaFeature,
  BetaFeatureStatus,
  DismissOrigin,
  Whitelist
} from "@tvg/sh-lib-beta-program/types";
import { PAGE } from "../../types";
import { QA_LABELS } from "../../utils/constants";
import { BetaFeature as StoryblokBetaFeature } from "../BetaFeaturesModal/types";

export const closeModal = (dispatch: ReduxDispatch) => () => {
  dispatch(closeBetaModal());
};

export const closeModalAndDismissFeature =
  (
    dispatch: ReduxDispatch,
    accountNumber: string,
    whitelist: Whitelist[],
    dismissEnrollModal: string | undefined,
    origin: DismissOrigin,
    betaFeature: BetaFeature
  ) =>
  () => {
    // Workaround to avoid the double execution of the fn below triggered by DS modal
    if (!hasDismissedBetaFeatureEnroll(accountNumber, BetaFeature.PROGRAM_PAGE))
      saveDismissStatus(accountNumber, betaFeature, whitelist, origin);

    dispatch(closeBetaModal());
    if (origin !== DismissOrigin.LATER) {
      ToastManager.show({
        variant: "informational",
        message: dismissEnrollModal || "",
        hasDismissButton: true
      });
    }
  };

export const handleOnClose =
  (
    origin: DismissOrigin,
    page: PAGE,
    dispatch: ReduxDispatch,
    accountNumber: string,
    betaFeature: BetaFeature,
    whitelist: Whitelist[],
    message: string | undefined
  ) =>
  () => {
    if (page === PAGE.WELCOME) {
      closeModalAndSetWelcomed(dispatch, accountNumber, betaFeature);
    } else {
      closeModalAndDismissFeature(
        dispatch,
        accountNumber,
        whitelist,
        message,
        origin,
        betaFeature
      )();
    }
  };

export const openModal = (dispatch: ReduxDispatch) => () =>
  dispatch(openBetaModal());

export const openModalOnMount =
  (
    page: PAGE,
    user: User,
    whitelist: Whitelist[],
    dispatch: ReduxDispatch,
    betaFeature: BetaFeature,
    betaFeatureInfo: StoryblokBetaFeature | null
  ) =>
  () => {
    if (
      (!!betaFeatureInfo && page === PAGE.WELCOME) ||
      (isEligible(user.accountNumber, whitelist, betaFeature) &&
        !isOptedInBetaFeature(user.accountNumber, betaFeature) &&
        !isFeatureInCooldown(
          user.accountNumber,
          betaFeature,
          BetaFeatureStatus.UNENROLL
        ) &&
        !isFeatureInCooldown(
          user.accountNumber,
          betaFeature,
          BetaFeatureStatus.DISMISS
        ))
    ) {
      clearStatusFromFeature(
        betaFeature,
        user.accountNumber,
        BetaFeatureStatus.DISMISS
      );
      openModal(dispatch)();
    }
  };

export const executeCTAGtmByPage = (action: ModalAction, page: PAGE) => () => {
  switch (page) {
    case PAGE.ENROLL:
      gtmClickCTAOnboardingModal(action.text);
      break;
    case PAGE.LEARN_MORE:
      gtmClickCTALearnMoreModal(action.text);
      break;
    case PAGE.WELCOME:
      gtmClickCTALetsStartModal();
      break;
    default:
      break;
  }
};

export const executeGtmByView =
  (isOpen: boolean, page: PAGE, betaFeature: BetaFeature) => () => {
    if (isOpen) {
      switch (page) {
        case PAGE.ENROLL:
          gtmViewOnboardingModal(betaFeature);
          break;
        case PAGE.LEARN_MORE:
          gtmViewLearnMoreModal();
          break;
        case PAGE.WELCOME:
          gtmViewWelcomeModal(betaFeature);
          break;
        default:
          break;
      }
    }
  };

export const goToLearnMore =
  (setPage: Dispatch<SetStateAction<PAGE>>, executeGtm: NullaryFn<void>) =>
  () => {
    executeGtm();
    setPage(PAGE.LEARN_MORE);
  };

export const enrollBetaFeature =
  (
    dispatch: ReduxDispatch,
    accountNumber: string,
    whitelist: Whitelist[],
    betaFeature: BetaFeature,
    executeGtm: NullaryFn<void>,
    redirectUrl?: string
  ) =>
  () => {
    executeGtm();
    enrollInBeta(accountNumber, whitelist, betaFeature, redirectUrl);
    closeModal(dispatch)();
  };

export const closeModalWithGtm =
  (dispatch: ReduxDispatch, executeGtm: NullaryFn<void>) => () => {
    closeModal(dispatch)();
    executeGtm();
  };

export const dismissLaterWithGtm =
  (
    dispatch: ReduxDispatch,
    accountNumber: string,
    whitelist: Whitelist[],
    dismissEnrollModal: string | undefined,
    origin: DismissOrigin,
    executeGtm: NullaryFn<void>,
    betaFeature: BetaFeature
  ) =>
  () => {
    closeModalAndDismissFeature(
      dispatch,
      accountNumber,
      whitelist,
      dismissEnrollModal,
      origin,
      betaFeature
    )();
    executeGtm();
  };

export const startBetaFeatureAndSetWelcomed =
  (
    page: PAGE,
    dispatch: ReduxDispatch,
    accountNumber: string,
    action: ModalAction,
    betaFeature: BetaFeature
  ) =>
  () => {
    saveWelcomedStatus(accountNumber, betaFeature);
    closeModalWithGtm(dispatch, executeCTAGtmByPage(action, page))();
  };

export const closeModalAndSetWelcomed = (
  dispatch: ReduxDispatch,
  accountNumber: string,
  betaFeature: BetaFeature
) => {
  // Workaround to avoid the double execution of the fn below triggered by DS modal
  if (!hasBeenWelcomedOnBetaFeature(accountNumber, betaFeature)) {
    saveWelcomedStatus(accountNumber, betaFeature);
    closeModal(dispatch)();
  }
};

export const getCTAHandler = (
  action: ModalAction,
  page: PAGE,
  setPage: Dispatch<SetStateAction<PAGE>>,
  dispatch: ReduxDispatch,
  accountNumber: string,
  whitelist: Whitelist[],
  betaFeature: BetaFeature,
  dismissEnrollModal: string | undefined,
  redirectUrl?: string
) => {
  switch (action.type) {
    case PAGE_ACTION.CONFIRM:
      return enrollBetaFeature(
        dispatch,
        accountNumber,
        whitelist,
        betaFeature,
        executeCTAGtmByPage(action, page),
        redirectUrl
      );
    case PAGE_ACTION.CANCEL:
      return closeModalAndDismissFeature(
        dispatch,
        accountNumber,
        whitelist,
        dismissEnrollModal,
        DismissOrigin.CROSS,
        betaFeature
      );
    case PAGE_ACTION.LEARN_MORE:
      return goToLearnMore(setPage, executeCTAGtmByPage(action, page));
    case PAGE_ACTION.LATER:
      return dismissLaterWithGtm(
        dispatch,
        accountNumber,
        whitelist,
        dismissEnrollModal,
        DismissOrigin.LATER,
        executeCTAGtmByPage(action, page),
        betaFeature
      );
    case PAGE_ACTION.START:
      return startBetaFeatureAndSetWelcomed(
        page,
        dispatch,
        accountNumber,
        action,
        betaFeature
      );
    default:
      return goToPage(PAGE.ENROLL, setPage);
  }
};

export const buildCTA = (
  actions: ModalAction[] | undefined,
  page: PAGE,
  setPage: Dispatch<SetStateAction<PAGE>>,
  dispatch: ReduxDispatch,
  accountNumber: string,
  whitelist: Whitelist[],
  betaFeature: BetaFeature,
  dismissEnrollModal: string | undefined,
  redirectUrl?: string
) =>
  (actions || []).map((action, index) => (
    <Button
      qaLabel={`${QA_LABELS.CTA}-${action.type}`}
      variant={action.variant || "primary"}
      onPress={getCTAHandler(
        action,
        page,
        setPage,
        dispatch,
        accountNumber,
        whitelist,
        betaFeature,
        dismissEnrollModal,
        redirectUrl
      )}
      key={`${action.text}-${action.type}-${
        action.variant
      }-${index.toString()}`}
    >
      {action.text || ""}
    </Button>
  ));

export const buildCTAObject = (
  text: string,
  type: PAGE_ACTION,
  variant: ButtonVariant
): ModalAction => ({
  text,
  type,
  variant
});

/**
 * Replaces all buttons of type LEARN MORE for a button of type LATER
 * @param actions CTA's array from modal
 * @returns Updated CTA's array without learn more actions and a new later action
 */
export const replaceLearnMoreCTA = (actions: ModalAction[] | undefined) => {
  const updatedActions: ModalAction[] = [];
  let laterCTA: ModalAction | null = null;

  (actions || []).forEach((item) => {
    // Removes all Learn more actions
    if (item.type !== PAGE_ACTION.LEARN_MORE) updatedActions.push(item);
    // Searches for any Later actions
    if (item.type === PAGE_ACTION.LATER) laterCTA = item;
  });

  // A new one is added if none exists
  if (!laterCTA)
    return [
      buildCTAObject("Maybe Later", PAGE_ACTION.LATER, "secondary"),
      ...updatedActions
    ];

  return updatedActions;
};

export const goToPage =
  (page: PAGE, setPage: Dispatch<SetStateAction<PAGE>>) => () =>
    setPage(page);
